"use client";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "next-view-transitions";
import { useLocale, useTranslations } from "next-intl";
import { useState, ReactNode } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";

type TitleKey =
  | "afterburn_title"
  | "power_dialer_title"
  | "call_documentator_title"
  | "workflows_title"
  | "dashboards_title"
  | "dynamic_scripts_title"
  | "scheduling_title"
  | "roles_title"
  | "custom_lists_title"
  | "api_title"
  | "email_title"
  | "custom_fields_title"
  | "telephony_title"
  | "close_import_title";
type DescKey =
  | "afterburn_desc"
  | "power_dialer_desc"
  | "call_documentator_desc"
  | "workflows_desc"
  | "dashboards_desc"
  | "dynamic_scripts_desc"
  | "scheduling_desc"
  | "roles_desc"
  | "custom_lists_desc"
  | "api_desc"
  | "email_desc"
  | "custom_fields_desc"
  | "telephony_desc"
  | "close_import_desc";

export type FeatureDef = {
  slug: string;
  titleKey: TitleKey;
  descKey: DescKey;
  icon: ReactNode;
  accent: string;
};

const FlameIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden>
    <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
  </svg>
);

const PhoneBoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden>
    <path d="M6.6 10.8c1.5 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C9.8 21 3 14.2 3 5.9c0-.6.4-1 1-1h3.4c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.4 0 .7-.2 1l-2.2 1.4z" />
    <path d="M17 2l-4 6h4l-3 6 8-8h-5l4-4z" opacity="0.5" />
  </svg>
);

const DocIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <path d="M4 5a2 2 0 0 1 2-2h9l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5Z" />
    <path d="M15 3v4a2 2 0 0 0 2 2h4" />
    <path d="M8 13h8M8 17h5" />
  </svg>
);

const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden>
    <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
  </svg>
);

const ChartIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <path d="M4 20V8M10 20V4M16 20v-9M22 20H2" />
  </svg>
);

const ScriptIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <path d="M6 3h11a2 2 0 0 1 2 2v13a3 3 0 0 0 3 3H8a3 3 0 0 1-3-3V5a2 2 0 0 1 1-2z" />
    <path d="M9 8h7M9 12h7M9 16h4" />
  </svg>
);

const CalendarMenuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 10h18" />
  </svg>
);

const ShieldMenuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" />
  </svg>
);

const ListsMenuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <path d="M3 5h18l-7 9v6l-4-2v-4z" />
  </svg>
);

const ApiMenuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <path d="m8 7-5 5 5 5M16 7l5 5-5 5M14 4l-4 16" />
  </svg>
);

const EnvelopeMenuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const FieldsMenuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <rect x="3" y="6" width="18" height="4" rx="1" />
    <rect x="3" y="14" width="14" height="4" rx="1" />
  </svg>
);

const SwitchMenuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <path d="M4 8h13l-3-3" />
    <path d="M20 16H7l3 3" />
  </svg>
);

const HeadsetMenuIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden
  >
    <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
    <rect x="3" y="14" width="4" height="6" rx="1" />
    <rect x="17" y="14" width="4" height="6" rx="1" />
  </svg>
);

const accent = "text-red-500 bg-neutral-900 ring-neutral-800";

export const FEATURES: FeatureDef[] = [
  {
    slug: "afterburn",
    titleKey: "afterburn_title",
    descKey: "afterburn_desc",
    icon: <FlameIcon />,
    accent,
  },
  {
    slug: "call-documentator",
    titleKey: "call_documentator_title",
    descKey: "call_documentator_desc",
    icon: <DocIcon />,
    accent,
  },
  {
    slug: "workflows",
    titleKey: "workflows_title",
    descKey: "workflows_desc",
    icon: <BoltIcon />,
    accent,
  },
  {
    slug: "dashboards",
    titleKey: "dashboards_title",
    descKey: "dashboards_desc",
    icon: <ChartIcon />,
    accent,
  },
  {
    slug: "scheduling",
    titleKey: "scheduling_title",
    descKey: "scheduling_desc",
    icon: <CalendarMenuIcon />,
    accent,
  },
  {
    slug: "email",
    titleKey: "email_title",
    descKey: "email_desc",
    icon: <EnvelopeMenuIcon />,
    accent,
  },
  {
    slug: "role-management",
    titleKey: "roles_title",
    descKey: "roles_desc",
    icon: <ShieldMenuIcon />,
    accent,
  },
  {
    slug: "api",
    titleKey: "api_title",
    descKey: "api_desc",
    icon: <ApiMenuIcon />,
    accent,
  },
  {
    slug: "custom-fields",
    titleKey: "custom_fields_title",
    descKey: "custom_fields_desc",
    icon: <FieldsMenuIcon />,
    accent,
  },
  {
    slug: "custom-lists",
    titleKey: "custom_lists_title",
    descKey: "custom_lists_desc",
    icon: <ListsMenuIcon />,
    accent,
  },
  {
    slug: "power-dialer",
    titleKey: "power_dialer_title",
    descKey: "power_dialer_desc",
    icon: <PhoneBoltIcon />,
    accent,
  },
  {
    slug: "dynamic-scripts",
    titleKey: "dynamic_scripts_title",
    descKey: "dynamic_scripts_desc",
    icon: <ScriptIcon />,
    accent,
  },
  {
    slug: "telephony",
    titleKey: "telephony_title",
    descKey: "telephony_desc",
    icon: <HeadsetMenuIcon />,
    accent,
  },
  {
    slug: "close-import",
    titleKey: "close_import_title",
    descKey: "close_import_desc",
    icon: <SwitchMenuIcon />,
    accent,
  },
];

export const FeaturesMenu = () => {
  const nav = useTranslations("nav");
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const isActive = pathname?.includes("/features");

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={cn(
          "flex items-center gap-1 text-sm leading-[110%] px-4 py-2 rounded-md hover:bg-[#F5F5F5] dark:hover:bg-neutral-800 hover:text-black text-muted dark:text-muted-dark",
          (isActive || open) && "bg-gray-100 dark:bg-neutral-800 text-black dark:text-white"
        )}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>{nav("features")}</span>
        <svg
          viewBox="0 0 12 12"
          className={cn(
            "h-3 w-3 transition-transform",
            open && "rotate-180"
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="m3 4.5 3 3 3-3" />
        </svg>
      </button>

      <AnimatePresence>
        {open && <FeaturesDropdown onSelect={() => setOpen(false)} />}
      </AnimatePresence>
    </div>
  );
};

const FeaturesDropdown = ({ onSelect }: { onSelect: () => void }) => {
  const fm = useTranslations("featuresMenu");
  const locale = useLocale();
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className="absolute left-1/2 -translate-x-1/2 top-full z-50"
    >
      {/* Transparent hover bridge so mouse can travel from button to panel */}
      <div className="h-3 w-full" />

      <div className="w-[680px] max-w-[calc(100vw-2rem)] rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-2xl overflow-hidden">
        <div className="px-5 pt-4 pb-3 border-b border-neutral-100 dark:border-neutral-800">
          <p className="text-sm font-semibold text-neutral-900 dark:text-white">
            {fm("heading")}
          </p>
          <p className="text-xs text-neutral-500 mt-0.5">{fm("subheading")}</p>
        </div>
        <ul className="p-2 grid grid-cols-2 gap-1">
          {FEATURES.map((f) => (
            <li key={f.slug}>
              <Link
                href={`/${locale}/features/${f.slug}`}
                onClick={onSelect}
                className="group flex h-full items-start gap-3 rounded-xl p-3 hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors"
              >
                <span
                  className={cn(
                    "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ring-1",
                    f.accent
                  )}
                >
                  {f.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                      {fm(f.titleKey)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                    {fm(f.descKey)}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};
